<template>
  <div class="app-container">
    <el-button
      type="primary"
      size="mini"
      style="margin-bottom: 10px"
      v-if="isMobile()"
      @click="handleReturnClick()"
    >
      {{ t('route.returnApprovalForm') }}
    </el-button>
    <Container
      :title="t('Acceptance.approveDetail')"
      :hasLine="true"
      style="min-height: 600px"
    >
      <template #main>
        <div style="margin: 10px 0">
          {{ t('Acceptance.acceptedBillsPendingApproval') }}
        </div>
        <div class="accepted-bills-table">
          <SimpleTable
            ref="tableRef"
            :listLoading="listLoading"
            :tableData="tableData"
            :columnStruct="columnStruct"
            :hadSelect="false"
            :hadOperate="false"
          />
        </div>
        <div class="relative-div">
          <el-form
            label-width="680px"
            ref="cFormRef"
            label-position="left"
            style="margin-left:2%; font-size: 14px;"
          >
            <el-row :gutter="4">
              <el-form-item
                :label="t('Acceptance.AcceptanceTotalAmount')"
                label-width="160px"
                style="text-align: left;"
              >
                <span>{{ formatThousands(totalDraftAmt) }}</span>
              </el-form-item>
            </el-row>
            <el-row :gutter="4">
              <el-form-item
                :label="t('Acceptance.approvalType')"
                label-width="160px"
                style="text-align: left;"
              >
                <span>{{ receiveType }}</span>
              </el-form-item>
            </el-row>
          </el-form>
        </div>
      </template>
    </Container>
    <div
      v-if="($hasPermission(['ACC_PLAN_W']) || accountIsApprover) && showFooterRefuseAndSignature"
      style="text-align: right;margin-right: 10%;"
    >
      <el-button
        type="primary"
        @click="rejectedClick"
      >
        {{ t('approvalForm.rejected') }}
      </el-button>
      <el-button
        type="primary"
        @click="approvedClick"
      >
        {{ t('approvalForm.approved') }}
      </el-button>
    </div>
    <el-dialog
      :title="t('Acceptance.rejectionReason')"
      :width="dialogWidth"
      v-model="commentsDialogVisible"
    >
      <el-input
        v-model="comments"
        type="textarea"
        rows="5"
        maxlength="100"
        show-word-limit
      />
      <div style="display: flex; align-items: center; padding: 5px;">
        <i class="el-icon-warning rejection-reason-tip" />
        <span>{{ t('Acceptance.rejectionReasonTip') }}</span>
      </div>
      <div
        class="dialog-footer"
        style="text-align: right;"
      >
        <el-button @click="commentsDialogVisible = false">
          {{ t('table.cancel') }}
        </el-button>
        <el-button
          type="primary"
          @click="onConfirm"
        >
          {{ t('Acceptance.confirmedDismissal') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { formatDate, formatThousands } from '@/utils'
import { formatDraftType } from '@/utils/acceptanceBillFormatUtil'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from '@/store'
import {
  approvalGranted, approvalRejected, getApprovalDetails
} from '@/apis/accepted-bill/acceptanceReceipt'
import Container from '@/components/form/container/Container.vue'
import { ElMessage } from 'element-plus'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import router from '@/router'
import {
  RejectionAcceptanceParamModel,
  SubmittedAcceptanceBillListItemModel
} from '@/model/accepted-bill/approvalFormModel'
import resize from '@/layout/resize'

export default defineComponent({
  name: 'AcceptanceApprovalHistoryDetail',
  components: {
    SimpleTable,
    Container
  },
  methods: { formatThousands },
  setup() {
    const { t } = useI18n()
    const tableRef = ref()
    const store = useStore()
    const id = useRoute().params.id.toString()
    const { isMobile } = resize()
    const dialogWidth = computed(() => {
      return isMobile() ? '100%' : '40%'
    })
    const userName = computed(() => {
      return store.state.user.info ? store.state.user.info.userAccount : ''
    })
    const detailTableStruct = [
      { prop: 'id', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'requestObject', label: t('Acceptance.requestObject'), width: 270 },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), width: 270 },
      {
        prop: 'requestDate',
        label: t('Acceptance.requestDate'),
        formatter: (row: SubmittedAcceptanceBillListItemModel) => formatDate(row.requestDate, 'yyyy-MM-dd')
      },
      {
        prop: 'draftType',
        label: t('Acceptance.draftType'),
        formatter: (row: SubmittedAcceptanceBillListItemModel) => formatDraftType(row.draftType),
        width: 120
      },
      { prop: 'bankName', label: t('Acceptance.bankName'), width: 270 },
      {
        prop: 'draftAmt',
        label: t('Acceptance.draftAmt'),
        width: 120,
        formatter: (row: any) => formatThousands(row.draftAmt),
        align: 'right'
      }
    ]
    const dataMap = reactive({
      tableData: Array<SubmittedAcceptanceBillListItemModel>(),
      columnStruct: detailTableStruct,
      listLoading: true,
      commentsDialogVisible: false,
      comments: '',
      totalDraftAmt: '',
      receiveType: '',
      showFooterRefuseAndSignature: false,
      accountIsApprover: false,
      async getData() {
        dataMap.listLoading = true
        let approvalId
        try {
          approvalId = Number(id)
        } catch (error) {
          ElMessage({
            message: 'id 格式错误;' + id,
            type: 'error',
            duration: 2000
          })
          return
        }
        const data = await getApprovalDetails(approvalId)
        if (data && data.data) {
          dataMap.totalDraftAmt = data.data.totalDraftAmt
          switch (data.data.receiveType) {
            case 1:
              dataMap.receiveType = t('Acceptance.signForReceipt')
              break
            case 2:
              dataMap.receiveType = t('Acceptance.applicationRejected')
              break
            default:
              dataMap.receiveType = ''
              break
          }
          dataMap.tableData = data.data.acceptanceBillList
          const approvers = data.data.approver.split(' / ')
          dataMap.accountIsApprover = approvers.includes(userName.value)
          dataMap.showFooterRefuseAndSignature = data.data.status > 0 && data.data.status < 4
        } else {
          dataMap.totalDraftAmt = ''
          dataMap.receiveType = ''
          dataMap.tableData = []
          dataMap.showFooterRefuseAndSignature = false
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      async handleReturnClick() {
        if (isMobile()) {
          await router.push({ name: 'ApproveAcceptancePageList' })
        }
      }
    })
    const rejectedClick = async() => {
      dataMap.commentsDialogVisible = true
    }
    const onConfirm = async() => {
      let approvalId
      try {
        approvalId = Number(id)
      } catch (error) {
        ElMessage({
          message: 'id 格式错误;' + id,
          type: 'error',
          duration: 2000
        })
        return
      }
      const params: RejectionAcceptanceParamModel = {
        id: approvalId,
        comments: dataMap.comments
      }
      const res = await approvalRejected(params)
      if (res?.data && res?.data > 0) {
        ElMessage({
          message: t('common.operateSuccess'),
          type: 'success',
          duration: 2000
        })
        await router.push({ name: 'ApproveAcceptancePageList', state: { refresh: true } })
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
    }
    const approvedClick = async() => {
      let approvalId
      try {
        approvalId = Number(id)
      } catch (error) {
        ElMessage({
          message: 'id 格式错误;' + id,
          type: 'error',
          duration: 2000
        })
        return
      }
      const res = await approvalGranted(approvalId)
      if (res?.data && res?.data > 0) {
        ElMessage({
          message: t('common.operateSuccess'),
          type: 'success',
          duration: 2000
        })
        await router.push({ name: 'ApproveAcceptancePageList', state: { refresh: true } })
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
    }
    onMounted(() => {
      dataMap.getData()
    })
    return {
      t,
      tableRef,
      rejectedClick,
      approvedClick,
      onConfirm,
      isMobile,
      dialogWidth,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.accepted-bills-table {
  width: 90%;
}

.relative-div {
  position: relative;
  top: 40px; /* 向下移动20px */
}

.rejection-reason-tip {
  font-size: 40px;
  color: #ff4949;
  padding-right: 10px;
}

@media (max-width: 768px) {
  .accepted-bills-table {
    width: 100%;
  }
}
</style>
